import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { InboxNotificationPayload } from '@aline/common';
import {
  getCrmRecordCopy,
  getServiceIdFromIntegration,
  getWorkflowRoute,
  Select,
} from '@aline/common';
import { alpha, Button, ButtonBase, Collapse, Typography } from '@mui/material';
import {
  Bell,
  BellDot,
  Check,
  CircleAlert,
  CircleCheck,
  Info,
  Mail,
  Trash2,
  TriangleAlert,
  Wrench,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { INTEGRATIONS_SECTION } from 'src/components/admin/integrations/integrations-nav-sections';
import { getIntegrationsSectionLink } from 'src/components/admin/integrations/integrations-section-spy';
import type { AlertsPopperRowMenuItem } from 'src/components/generic/AlertsPopper';
import {
  AlertsPopperButton,
  AlertsPopperRow,
  POPPER_RULE_COLOR,
} from 'src/components/generic/AlertsPopper';
import { CrmConnectRequirementsHost } from 'src/components/send-for-signature/components/AddIntegration';
import { Apollo } from 'src/gql/generated';
import { INTEGRATION_DISPLAY_NAME } from 'src/helpers/integration-display-name';
import type { InboxNotificationItem } from 'src/hooks/inbox-notifications';
import {
  useInboxNotificationActions,
  useInboxNotifications,
  useInboxNotificationsLive,
} from 'src/hooks/inbox-notifications';
import { useAlineCrmSession } from 'src/hooks/integrations';
import { appColors } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

// Elapsed times only need minute precision past the first minute, so a slow tick is enough.
const CLOCK_TICK_MS = 30_000;

// A page more than fills the list, so there is something to scroll; nearing the end reveals the next.
const PAGE_SIZE = 10;

const ROW_TRANSITION_MS = 200;

// In chip order.
const INBOX_FILTERS = ['unread', 'all', 'errors', 'warnings'] as const;
type InboxFilter = (typeof INBOX_FILTERS)[number];

// What needs attention first. The bell starts here, then keeps the viewer's pick until a reload.
const DEFAULT_FILTER: InboxFilter = 'unread';

interface InboxFilterRule {
  readonly label: string;
  readonly matches: (item: InboxNotificationItem) => boolean;
}

const INBOX_FILTER = {
  all: { label: 'All', matches: () => true },
  errors: {
    label: 'Errors',
    matches: item => item.severity === Apollo.InboxNotificationSeverity.Error,
  },
  unread: {
    label: 'Unread',
    matches: item => !item.read,
  },
  warnings: {
    label: 'Warnings',
    matches: item => item.severity === Apollo.InboxNotificationSeverity.Warning,
  },
} satisfies Record<InboxFilter, InboxFilterRule>;

// In list order, newest first.
const DAY_GROUPS = ['today', 'yesterday', 'lastWeek', 'lastMonth', 'older'] as const;
type DayGroup = (typeof DAY_GROUPS)[number];

const DAY_GROUP_LABEL = {
  lastMonth: 'Last month',
  lastWeek: 'Last week',
  older: 'Older',
  today: 'Today',
  yesterday: 'Yesterday',
} satisfies Record<DayGroup, string>;

/**
 * How many local calendar days back each group reaches, counting today as 0, newest first. Last
 * week and last month are rolling (the past 7 and 30 days), not calendar weeks and months.
 */
const DAY_GROUP_MAX_DAYS_AGO = [
  { daysAgo: 0, group: 'today' },
  { daysAgo: 1, group: 'yesterday' },
  { daysAgo: 7, group: 'lastWeek' },
  { daysAgo: 30, group: 'lastMonth' },
] as const satisfies readonly { daysAgo: number; group: DayGroup }[];

const ELAPSED_UNITS = [
  { label: 'w', seconds: 7 * 24 * 60 * 60 },
  { label: 'd', seconds: 24 * 60 * 60 },
  { label: 'h', seconds: 60 * 60 },
  { label: 'm', seconds: 60 },
] as const;

interface SeverityIcon {
  readonly background: string;
  readonly color: string;
  readonly icon: React.ReactNode;
}

// A pale disc in the icon's own hue, as in the design; a plain wash of amber reads as brown.
const SEVERITY_ICON = {
  [Apollo.InboxNotificationSeverity.Error]: {
    background: '#FEEBEC',
    color: '#E5484D',
    icon: <CircleAlert strokeWidth={2} />,
  },
  [Apollo.InboxNotificationSeverity.Info]: {
    background: alpha(appColors.primary, 0.1),
    color: appColors.primary,
    icon: <Info strokeWidth={2} />,
  },
  [Apollo.InboxNotificationSeverity.Success]: {
    background: alpha(appColors.connectedCheck, 0.1),
    color: appColors.connectedCheck,
    icon: <CircleCheck strokeWidth={2} />,
  },
  [Apollo.InboxNotificationSeverity.Warning]: {
    background: '#FEF3C7',
    color: '#D97706',
    icon: <TriangleAlert strokeWidth={2} />,
  },
} satisfies Record<Apollo.InboxNotificationSeverity, SeverityIcon>;

// Problems to act on get "Fix issue" first in their "..." menu; the rest only report something.
const SEVERITY_OFFERS_FIX = {
  [Apollo.InboxNotificationSeverity.Error]: true,
  [Apollo.InboxNotificationSeverity.Info]: false,
  [Apollo.InboxNotificationSeverity.Success]: false,
  [Apollo.InboxNotificationSeverity.Warning]: true,
} satisfies Record<Apollo.InboxNotificationSeverity, boolean>;

interface CrmIssueCopy {
  readonly fix: (crmName: string) => string;
  readonly title: (crmName: string) => string;
}

/**
 * What each CRM problem means and the one thing that clears it. Every kind the server can report
 * has an entry, so a new kind fails to compile until it has copy.
 */
const CRM_ISSUE_COPY = {
  [Apollo.CrmConnectionIssueKind.AuthorizationExpired]: {
    fix: crmName => `Reconnect with the same ${crmName} admin account.`,
    title: crmName => `${crmName} sign-in has expired`,
  },
  [Apollo.CrmConnectionIssueKind.MissingScopes]: {
    fix: crmName =>
      `Reconnect ${crmName} to grant the missing access. If this message comes back, contact Aline support.`,
    title: crmName => `${crmName} connection is missing access`,
  },
  [Apollo.CrmConnectionIssueKind.PackageSecretUndelivered]: {
    fix: crmName =>
      `${crmName} can't send updates to Aline yet. Update the Aline package to the latest version, make sure the user who connected has the Aline Admin permission set, then reconnect. If this message comes back, contact Aline support.`,
    title: crmName => `${crmName} package out of date`,
  },
  [Apollo.CrmConnectionIssueKind.SetupIncomplete]: {
    fix: () => 'Complete every step in the connection window.',
    title: crmName => `${crmName} connection is incomplete`,
  },
} satisfies Record<Apollo.CrmConnectionIssueKind, CrmIssueCopy>;

const useStyles = makeStyles()(theme => ({
  chip: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'flex-start',
    border: `1px solid ${POPPER_RULE_COLOR}`,
    borderRadius: 999,
    color: theme.appColors.secondaryType,
    display: 'flex',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 400,
    lineHeight: '16px',
    padding: theme.spacing(0.75, 1.5),
    transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  chipSelected: {
    '&:hover': {
      backgroundColor: alpha(theme.appColors.primary, 0.12),
    },
    backgroundColor: alpha(theme.appColors.primary, 0.08),
    borderColor: 'transparent',
    color: theme.appColors.primary,
    fontWeight: 600,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  empty: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  emptyIcon: {
    alignItems: 'center',
    backgroundColor: alpha(theme.appColors.primary, 0.1),
    borderRadius: '50%',
    color: theme.appColors.primary,
    display: 'flex',
    height: 40,
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    width: 40,
  },
  emptyText: {
    color: '#737380',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 'normal',
  },
  emptyTitle: {
    color: theme.appColors.iconsType,
    fontSize: 15,
    fontWeight: 600,
    lineHeight: '20px',
  },
  // On the cards' left edge, so the label and the rows under it share one line.
  groupLabel: {
    color: theme.appColors.secondaryType,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    lineHeight: '16px',
    padding: theme.spacing(1, 0),
    textTransform: 'uppercase',
  },
  // Scrolls inside the popper instead of running off the screen.
  list: {
    maxHeight: 480,
    overflowY: 'auto',
  },
  /**
   * Pulled in by its own padding, so its label sits on the header's 16px inset and the button
   * does not make the header taller than its title.
   */
  markAllRead: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '18px',
    margin: theme.spacing(-0.25, -0.75),
    minWidth: 0,
    padding: theme.spacing(0.25, 0.75),
    textTransform: 'none',
  },
  // Watched at the bottom of the list: scrolling it into view reveals the next page.
  sentinel: {
    height: 1,
  },
}));

/**
 * The header's bell: the org's notifications, with the viewer's unread count on the badge. Opening
 * or closing it reads nothing; a row is read only when the viewer opens it or marks it read.
 */
export const NotificationBell = React.memo(function NotificationBell(): JSX.Element {
  const { classes, cx } = useStyles();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<InboxFilter>(DEFAULT_FILTER);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [list, setList] = useState<HTMLDivElement | null>(null);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const { items: allItems, unreadCount } = useInboxNotifications();
  const { dismiss, markAllRead, markUnread } = useInboxNotificationActions();
  // Only admins can open the CRM Vault; members fix a CRM issue from the integrations page instead.
  const { canConnectCrm, crmConnectRequest, openVault } = useAlineCrmSession();
  const reconnectCrm = useCallback(
    (integration: Apollo.Integration) => void openVault(getServiceIdFromIntegration(integration)),
    [openVault]
  );
  /**
   * Unread ids at the moment the bell opened. These stay under Unread until the bell closes, so a
   * row marked read keeps its place and can be marked unread again.
   */
  const [unreadAtOpenIds, setUnreadAtOpenIds] = useState<ReadonlySet<string>>(() => new Set());
  const matchesFilter = useCallback(
    (option: InboxFilter, item: InboxNotificationItem) => {
      if (option !== 'unread') return INBOX_FILTER[option].matches(item);
      return !item.read || unreadAtOpenIds.has(item.id);
    },
    [unreadAtOpenIds]
  );
  // Filtered before the leaving-rows pass, so a row read under "Unread" collapses away smoothly.
  const items = useMemo(
    () => allItems.filter(item => matchesFilter(filter, item)),
    [allItems, filter, matchesFilter]
  );
  const { entries, onExited } = useLeavingRows(items);
  const now = useClockWhileOpen(open);
  const hasMore = limit < items.length;
  const showNextPage = useCallback(() => setLimit(current => current + PAGE_SIZE), []);
  useRevealOnReachEnd({
    enabled: open && hasMore,
    list,
    onReachEnd: showNextPage,
    sentinel,
  });
  useInboxNotificationsLive();

  const showFirstPage = useCallback(() => {
    setLimit(PAGE_SIZE);
    list?.scrollTo({ top: 0 });
  }, [list]);

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (next === open) return;
      setOpen(next);
      if (!next) return;
      setUnreadAtOpenIds(new Set(allItems.filter(item => !item.read).map(item => item.id)));
      showFirstPage();
    },
    [allItems, open, showFirstPage]
  );
  const markRead = useCallback((item: InboxNotificationItem) => markAllRead([item]), [markAllRead]);
  // Clicking a row reads it, then closes the bell.
  const openItem = useCallback(
    (item: InboxNotificationItem) => {
      markRead(item);
      onOpenChange(false);
    },
    [markRead, onOpenChange]
  );
  const hasUnread = allItems.some(item => !item.read);

  const shownIds = new Set(items.slice(0, limit).map(item => item.id));
  // Newest first within each day, as the entries already are.
  const groups = DAY_GROUPS.map(group => ({
    entries: entries.filter(({ item }) => getDayGroup(new Date(item.createdAt), now) === group),
    group,
  }));

  return (
    <>
      <AlertsPopperButton
        keepMounted
        buttonDataCy={Select.globalTopBar.notificationsButton}
        count={unreadCount}
        headerAction={
          <Button
            className={classes.markAllRead}
            color="primary"
            disabled={!hasUnread}
            size="small"
            onClick={() => markAllRead(allItems)}
          >
            Mark all as read
          </Button>
        }
        headerToolbar={
          <div aria-label="Filter notifications" className={classes.chips} role="group">
            {INBOX_FILTERS.map(option => (
              <ButtonBase
                key={option}
                aria-pressed={filter === option}
                className={cx(classes.chip, filter === option && classes.chipSelected)}
                onClick={() => {
                  setFilter(option);
                  showFirstPage();
                }}
              >
                {`${INBOX_FILTER[option].label} (${allItems.filter(item => matchesFilter(option, item)).length})`}
              </ButtonBase>
            ))}
          </div>
        }
        icon={<Bell size={18} strokeWidth={1.5} />}
        label="Notifications"
        open={open}
        popperDataCy={Select.globalTopBar.notificationsPopper}
        onOpenChange={onOpenChange}
      >
        <Collapse in={items.length === 0} timeout={ROW_TRANSITION_MS}>
          <div className={classes.empty}>
            <span className={classes.emptyIcon}>
              <BellDot size={20} strokeWidth={1.75} />
            </span>
            <Typography className={classes.emptyTitle}>{"You're all caught up!"}</Typography>
            <Typography className={classes.emptyText}>
              {'No new notifications right now.'}
            </Typography>
          </div>
        </Collapse>
        <div ref={setList} className={classes.list}>
          {groups.map(({ entries: groupEntries, group }) => (
            <React.Fragment key={group}>
              <Collapse
                in={groupEntries.some(({ item, leaving }) => !leaving && shownIds.has(item.id))}
                timeout={ROW_TRANSITION_MS}
              >
                <Typography className={classes.groupLabel}>{DAY_GROUP_LABEL[group]}</Typography>
              </Collapse>
              {groupEntries.map(({ item, leaving }) => (
                <Collapse
                  key={item.id}
                  appear
                  in={!leaving && shownIds.has(item.id)}
                  timeout={ROW_TRANSITION_MS}
                  onExited={() => onExited(item.id)}
                >
                  <InboxNotificationRow
                    item={item}
                    now={now}
                    onDismiss={dismiss}
                    onMarkRead={markRead}
                    onMarkUnread={markUnread}
                    onOpen={openItem}
                    onReconnectCrm={canConnectCrm ? reconnectCrm : null}
                  />
                </Collapse>
              ))}
            </React.Fragment>
          ))}
          <div ref={setSentinel} aria-hidden className={classes.sentinel} />
        </div>
      </AlertsPopperButton>
      <CrmConnectRequirementsHost request={crmConnectRequest} />
    </>
  );
});

/** One notification in the bell, drawn from its kind's entry in `INBOX_KIND_VIEW`. */
function InboxNotificationRow({
  item,
  now,
  onDismiss,
  onMarkRead,
  onMarkUnread,
  onOpen,
  onReconnectCrm,
}: {
  readonly item: InboxNotificationItem;
  /** The clock the elapsed time is read against, so every row ages together. */
  readonly now: Date;
  readonly onDismiss: (id: string) => void;
  readonly onMarkRead: (item: InboxNotificationItem) => void;
  readonly onMarkUnread: (id: string) => void;
  /** Called when the viewer clicks the row, before it takes them elsewhere. */
  readonly onOpen: (item: InboxNotificationItem) => void;
  /** Opens a CRM's Vault to reconnect it; null for a viewer who can't. */
  readonly onReconnectCrm: ((integration: Apollo.Integration) => void) | null;
}): JSX.Element {
  const navigate = useNavigate();
  const severity: SeverityIcon = SEVERITY_ICON[item.severity];
  const time = formatElapsed(new Date(item.createdAt), now);
  const open = () => onOpen(item);
  const readAction: AlertsPopperRowMenuItem = item.read
    ? {
        icon: <Mail />,
        label: 'Mark as unread',
        onClick: () => onMarkUnread(item.id),
      }
    : {
        icon: <Check />,
        label: 'Mark as read',
        onClick: () => onMarkRead(item),
      };
  const removeAction: AlertsPopperRowMenuItem = {
    icon: <Trash2 />,
    label: 'Remove',
    onClick: () => onDismiss(item.id),
    tone: 'danger',
  };
  const menuItems = [readAction, removeAction];

  const view = getInboxRowView(item.parsed);
  const resolve = () => {
    open();
    navigate(view.route);
  };
  // A CRM connection issue is fixed by reconnecting, so "Fix issue" opens the Vault straight away.
  const crmToReconnect =
    item.parsed.kind === Apollo.InboxNotificationKind.CrmConnectionIssue
      ? item.parsed.payload.integration
      : null;
  const fix =
    onReconnectCrm && crmToReconnect
      ? () => {
          open();
          onReconnectCrm(crmToReconnect);
        }
      : resolve;

  return (
    <AlertsPopperRow
      dataCy={`inbox-notification-${item.kind}`}
      fullText={view.subtitle}
      icon={severity.icon}
      iconBackground={severity.background}
      iconColor={severity.color}
      menuItems={withFixAction(menuItems, item.severity, fix)}
      subtitle={view.subtitle}
      time={time}
      title={view.title}
      unread={!item.read}
      onClick={resolve}
    />
  );
}

/** What a row shows and where clicking it goes, derived from the notification's payload. */
interface InboxRowView {
  /** Where the problem is fixed, or where the notification's subject lives. */
  readonly route: string;
  readonly subtitle: string;
  readonly title: string;
}

type InboxKindViews = {
  readonly [TKind in Apollo.InboxNotificationKind]: (
    payload: InboxNotificationPayload<TKind>
  ) => InboxRowView;
};

/**
 * How each kind is drawn. Keyed on the kind enum, so a new kind fails to compile until it has a
 * view; adding one is the whole client-side cost of a new kind.
 */
const INBOX_KIND_VIEW: InboxKindViews = {
  // The CRM's card on the integrations page is where every CRM problem is fixed.
  [Apollo.InboxNotificationKind.CrmConnectionIssue]: payload => {
    const crmName = INTEGRATION_DISPLAY_NAME[payload.integration];
    const copy: CrmIssueCopy = CRM_ISSUE_COPY[payload.issueKind];
    const detail = payload.detail ? `${crmName} said: ${payload.detail} ` : '';
    return {
      route: getIntegrationsSectionLink(INTEGRATIONS_SECTION.crms),
      subtitle: `${detail}${copy.fix(crmName)}`,
      title: copy.title(crmName),
    };
  },
  [Apollo.InboxNotificationKind.CrmRecordDeleted]: payload => {
    const { label, lower } = getCrmRecordCopy(payload.integration);
    return {
      route: getWorkflowRoute(payload.workflowId),
      subtitle: `A workflow is linked to it. Unlink it and link another ${lower}.`,
      title: `${label} deleted in ${INTEGRATION_DISPLAY_NAME[payload.integration]}`,
    };
  },
};

// Generic over the kind, so the payload reaches the view that expects its shape.
function getInboxRowView<TKind extends Apollo.InboxNotificationKind>(parsed: {
  readonly kind: TKind;
  readonly payload: InboxNotificationPayload<TKind>;
}): InboxRowView {
  const view: InboxKindViews[TKind] = INBOX_KIND_VIEW[parsed.kind];
  return view(parsed.payload);
}

// "Fix issue" first in the menu, for the severities that have something to fix.
function withFixAction(
  menuItems: readonly AlertsPopperRowMenuItem[],
  severity: Apollo.InboxNotificationSeverity,
  fix: () => void
): readonly AlertsPopperRowMenuItem[] {
  if (!SEVERITY_OFFERS_FIX[severity]) return menuItems;
  return [{ icon: <Wrench />, label: 'Fix issue', onClick: fix }, ...menuItems];
}

/**
 * The current time, re-read on opening and then every tick while open, so "5m" does not sit
 * stale; a closed bell does not tick. Re-running with the same `open` only restarts the interval.
 */
function useClockWhileOpen(open: boolean): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!open) return;
    setNow(new Date());
    const interval = window.setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => window.clearInterval(interval);
  }, [open]);

  return now;
}

/**
 * Calls `onReachEnd` when the sentinel at the bottom of the list scrolls into view. The observer
 * fires as the sentinel enters, and the rows it reveals grow in and push it back out, so reaching
 * the end once reveals one page. A re-run with the same inputs only re-checks whether the end is
 * in view, which reveals a page only if one is needed to fill the list.
 */
function useRevealOnReachEnd({
  enabled,
  list,
  onReachEnd,
  sentinel,
}: {
  readonly enabled: boolean;
  readonly list: HTMLElement | null;
  readonly onReachEnd: () => void;
  readonly sentinel: HTMLElement | null;
}): void {
  useEffect(() => {
    if (!enabled || !list || !sentinel) return;
    const observer = new IntersectionObserver(
      observed => {
        if (observed.some(entry => entry.isIntersecting)) onReachEnd();
      },
      // A little early, so the next rows are in place before the viewer reaches the bottom.
      { root: list, rootMargin: '0px 0px 120px 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, list, onReachEnd, sentinel]);
}

interface RowEntry {
  readonly item: InboxNotificationItem;
  /** Gone from the inbox, but still on screen while its row collapses. */
  readonly leaving: boolean;
}

/**
 * The rows to draw: the current items, plus the ones just dismissed or resolved, kept in their
 * old place until their collapse finishes, so a row closes smoothly instead of vanishing.
 */
function useLeavingRows(items: readonly InboxNotificationItem[]) {
  const [previousItems, setPreviousItems] = useState(items);
  const [leaving, setLeaving] = useState<readonly RowEntry[]>([]);

  if (items !== previousItems) {
    const currentIds = new Set(items.map(item => item.id));
    const removed = previousItems.filter(item => !currentIds.has(item.id));
    setLeaving(current => [
      ...current.filter(entry => !currentIds.has(entry.item.id)),
      ...removed.map(item => ({ item, leaving: true })),
    ]);
    setPreviousItems(items);
  }

  const onExited = useCallback(
    (id: string) => setLeaving(current => current.filter(entry => entry.item.id !== id)),
    []
  );

  // Items arrive newest first, so a leaving row goes back to where it sat by creation time.
  const entries: RowEntry[] = [...items.map(item => ({ item, leaving: false })), ...leaving].sort(
    (a, b) => b.item.createdAt.localeCompare(a.item.createdAt)
  );

  return { entries, onExited };
}

// By local calendar day, so a notification from late last night is under Yesterday.
function getDayGroup(createdAt: Date, now: Date): DayGroup {
  const match = DAY_GROUP_MAX_DAYS_AGO.find(({ daysAgo }) => {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysAgo);
    return createdAt.getTime() >= start.getTime();
  });
  return match?.group ?? 'older';
}

/** How long ago, in the largest whole unit: "12s", "5m", "3h", "2d", "1w". */
function formatElapsed(from: Date, now: Date): string {
  const seconds = Math.max(0, Math.floor((now.getTime() - from.getTime()) / 1000));
  const unit = ELAPSED_UNITS.find(candidate => seconds >= candidate.seconds);
  if (!unit) return `${seconds}s`;
  return `${Math.floor(seconds / unit.seconds)}${unit.label}`;
}
